/**
 * Date-aware search utilities for Community and Gear Swap listings.
 *
 * Supports queries like:
 *   "August"               → any post with a calendar date in August
 *   "August 25"            → any post with Aug 25 on its calendar
 *   "Aug 25 to Sep 3"      → any post whose calendar overlaps that range
 *   "8/25"  or  "08-25"   → numeric date formats
 */

const MONTH_NAMES = {
  january: 1, february: 2, march: 3, april: 4, may: 5, june: 6,
  july: 7, august: 8, september: 9, october: 10, november: 11, december: 12,
  jan: 1, feb: 2, mar: 3, apr: 4, jun: 6, jul: 7, aug: 8,
  sep: 9, oct: 10, nov: 11, dec: 12,
};

/** Remove ordinal suffixes: "25th" → "25", "3rd" → "3" */
function stripOrdinal(s) {
  return s.replace(/(\d+)(st|nd|rd|th)\b/gi, '$1');
}

/**
 * Parse a single date-like fragment into { month, day? }.
 * Returns null if the fragment is not recognisable as a date.
 */
function parseSingleDateFragment(raw) {
  const s = stripOrdinal(raw.trim().toLowerCase());

  // "month day?" — e.g. "august 25", "aug", "august"
  for (const [name, monthNum] of Object.entries(MONTH_NAMES)) {
    if (s.startsWith(name)) {
      const rest = s.slice(name.length).trim();
      if (!rest) return { month: monthNum };
      const day = parseInt(rest, 10);
      if (day >= 1 && day <= 31) return { month: monthNum, day };
      return null; // month matched but trailing text isn't a day
    }
  }

  // Numeric "M/D" or "M-D" — e.g. "8/25", "08-25"
  const numericMatch = s.match(/^(\d{1,2})[\/\-](\d{1,2})$/);
  if (numericMatch) {
    const m = parseInt(numericMatch[1], 10);
    const d = parseInt(numericMatch[2], 10);
    if (m >= 1 && m <= 12 && d >= 1 && d <= 31) return { month: m, day: d };
  }

  return null;
}

/**
 * Attempt to parse the full query string as a date, specific date, or range.
 * Returns a structured object or null when the query is not date-like.
 *
 * Returned shapes:
 *   { type: 'month', month: N }
 *   { type: 'date',  month: N, day: N }
 *   { type: 'range', startMonth: N, startDay: N, endMonth: N, endDay: N }
 */
export function parseDateQuery(query) {
  const q = stripOrdinal(query.trim().toLowerCase());

  // Range: "august 25 to september 3"  /  "aug 25 - sep 3"
  const rangeParts = q.match(/^(.+?)\s+(?:to|\-)\s+(.+)$/);
  if (rangeParts) {
    const start = parseSingleDateFragment(rangeParts[1]);
    const end = parseSingleDateFragment(rangeParts[2]);
    if (start?.day && end?.day) {
      return {
        type: 'range',
        startMonth: start.month, startDay: start.day,
        endMonth: end.month,   endDay: end.day,
      };
    }
  }

  // Single date or month
  const single = parseSingleDateFragment(q);
  if (!single) return null;
  return single.day
    ? { type: 'date',  month: single.month, day: single.day }
    : { type: 'month', month: single.month };
}

/**
 * Given a YYYY-MM-DD string, return { month, day } (1-based, ignoring year).
 * Returns null for invalid strings.
 */
function extractMonthDay(dateStr) {
  if (!dateStr || typeof dateStr !== 'string') return null;
  // Handle both "YYYY-MM-DD" and bare ISO timestamps
  const clean = dateStr.slice(0, 10);
  const parts = clean.split('-');
  if (parts.length !== 3) return null;
  const m = parseInt(parts[1], 10);
  const d = parseInt(parts[2], 10);
  if (isNaN(m) || isNaN(d)) return null;
  return { month: m, day: d };
}

/** Encode { month, day } as a sortable integer (year-agnostic). */
function toMDNum({ month, day }) {
  return month * 100 + (day ?? 0);
}

/** True when a <= b in month-day space (no year wrapping). */
function mdLte(a, b) {
  return toMDNum(a) <= toMDNum(b);
}

/**
 * True when two month-day ranges overlap.
 * Both ranges are expressed as { month, day } pairs and are assumed NOT to
 * wrap across a year boundary. Ranges that do wrap (e.g. Dec→Jan) should be
 * rare for tour dates and are treated as non-wrapping for simplicity.
 */
function rangesOverlap(aStart, aEnd, bStart, bEnd) {
  // [aStart, aEnd] overlaps [bStart, bEnd] iff aStart <= bEnd && bStart <= aEnd
  return mdLte(aStart, bEnd) && mdLte(bStart, aEnd);
}

/**
 * Return true when at least one calendar widget in the entity's items[]
 * overlaps with the parsed date query.
 *
 * Key fix: a "specific date" (e.g. "june 3") matches any widget whose
 * date range CONTAINS that date, not just widgets whose stored start/end
 * date exactly equals it.
 */
export function entityMatchesDateQuery(entity, parsedDate) {
  if (!parsedDate) return false;
  const items = entity?.items;
  if (!Array.isArray(items)) return false;

  for (const item of items) {
    if (item?.type !== 'calendar' || !item.value) continue;

    let cal;
    try {
      cal = typeof item.value === 'string' ? JSON.parse(item.value) : item.value;
    } catch (_) {
      continue;
    }

    // Build the widget's effective range.
    // A single-date widget has the same start and end.
    const widgetStart = extractMonthDay(cal.startDate || cal.date);
    if (!widgetStart) continue;
    const widgetEnd = extractMonthDay(cal.endDate || cal.date) || widgetStart;

    if (parsedDate.type === 'month') {
      // Query: "june" — does the widget overlap with any day in June?
      const monthFirst = { month: parsedDate.month, day: 1 };
      const monthLast  = { month: parsedDate.month, day: 31 };
      if (rangesOverlap(widgetStart, widgetEnd, monthFirst, monthLast)) return true;

    } else if (parsedDate.type === 'date') {
      // Query: "june 3" — does June 3 fall anywhere inside the widget's range?
      const queryMD = { month: parsedDate.month, day: parsedDate.day };
      if (rangesOverlap(widgetStart, widgetEnd, queryMD, queryMD)) return true;

    } else if (parsedDate.type === 'range') {
      // Query: "june 1 to june 30" — does any part of the widget overlap?
      const qStart = { month: parsedDate.startMonth, day: parsedDate.startDay };
      const qEnd   = { month: parsedDate.endMonth,   day: parsedDate.endDay   };
      if (rangesOverlap(widgetStart, widgetEnd, qStart, qEnd)) return true;
    }
  }

  return false;
}
